/**
 * Participantes da sala (system/stdVertUserList; FC.Room.UIModule.StandardVerticalUserList).
 * No original: grupos por papel (mestres, jogadores, espectadores) e, para cada
 * pessoa, um cartão com avatar grande, nome, conta com estado de presença e,
 * quando houver personagem, barras de status e linhas de detalhes da ficha (M3).
 *
 * No alto, em blocos iguais, "Convidar amigos", "Falar com o mestre", "Conversa em grupo" e "Voz e
 * vídeo": chamar, juntar e falar com as pessoas fica onde elas estão (docs/spec/room-menus.md). "Voz
 * e vídeo" abre a conferência ali mesmo, acima da lista; só na mesa, que tem a chamada (não nas
 * janelas próprias).
 *
 * Clique direito (ou o botão ⋯) abre o menu de presença: perfil, a foto em tamanho grande, apelido e,
 * para quem administra a sala, papéis, modos, expulsar e banir
 * (docs/spec/room-roles.md). O mestre também cria uma ficha para a pessoa, dá a ela uma
 * ficha da biblioteca e escolhe o personagem principal dela (como os "personagens do
 * jogador" do original).
 *
 * As barrinhas se editam direto nelas, com duplo clique (ou Enter, ou um toque): cada um as
 * próprias, o mestre as de todos (`BarEditor`). As linhas também, no lugar do texto
 * (`LineEditor`). Quem pode editar vê também as barras e as linhas vazias.
 */
import { useCallback, useLayoutEffect, useRef, useState, type ComponentProps, type MouseEvent } from 'react';
import { useT } from '../../i18n/index.js';
import { useApp } from '../../store.js';
import { DEFAULT_GAME_RULES, MEMBER_LIMITS, barColor, barName, barText, canSeeBarNumbers, lineRule, roleOfModes, type GameRules, type RoomMember, type RoomRole, type User } from '../../shared.js';
import { Button, Dialog, TextEdit } from '../../ui/controls.js';
import { Icon, type IconName } from '../../ui/icons.js';
import { Avatar, shrinkAvatar } from '../../social/Avatar.js';
import { avatarSrc } from '../../social/avatarUrl.js';
import { ImageViewerDialog } from '../../ui/ImageViewer.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../../ui/ContextMenu.js';
import { askChoice, askConfirm, askText } from '../../ui/prompt.js';
import { toast } from '../../ui/Toaster.js';
import { useLongPress } from '../../ui/touch.js';
import { NewSheetDialog } from '../NewSheetDialog.js';
import { BarEditor } from '../BarEditor.js';
import { cardNamesOf, roomPeople, sheetsOf, sheetsToGive } from '../sheetOwners.js';
import { sheetSystems } from '../../plugins/pluginTypes.js';
import { useVideoOf } from '../../voice/videoContext.js';
import { useRoomVoice } from '../../voice/voiceContext.js';
import { ConferencePanel } from '../voice/ConferencePanel.js';
import { CARD_AVATAR_ALONE, CARD_AVATAR_MIN, cardAvatarSize } from '../cardAvatar.js';
import { InviteFriendsDialog } from '../../social/InviteFriendsDialog.js';
import { ChooseUsersDialog } from './PrivateChatModules.js';
import { POPOUT } from '../../windowMode.js';
import { openDm, useCanDm } from '../../social/dm.js';

const ROLE_ORDER: RoomRole[] = ['gm', 'player', 'spectator'];

export function UserListModule() {
  const t = useT();
  // a webcam de quem está com a câmera ligada na conferência, no lugar da foto (BRAZ-20)
  const videoOf = useVideoOf();
  const voice = useRoomVoice();
  const [voiceOpen, setVoiceOpen] = useState(false);
  const users = useApp((s) => s.users);
  const members = useApp((s) => s.members);
  const typing = useApp((s) => s.typing);
  const room = useApp((s) => s.room);
  const me = useApp((s) => s.user);
  const viewProfile = useApp((s) => s.viewProfile);
  const setRole = useApp((s) => s.setRole);
  const setFlag = useApp((s) => s.setFlag);
  const kick = useApp((s) => s.kick);
  const setNick = useApp((s) => s.setNick);
  const setRoomAvatar = useApp((s) => s.setRoomAvatar);
  const openPrivate = useApp((s) => s.openPrivate);
  const createGroup = useApp((s) => s.createGroup);
  // os convites (docs/spec/friends.md) só com um servidor que os entrega, e só na janela principal,
  // que tem a lista de amigos
  const canInvite = useApp((s) => s.serverFeatures.presence) && !POPOUT;
  // a conversa direta, embaixo (docs/spec/friends.md): com qualquer um, e com o dono da mesa pelo alto
  const canDm = useCanDm();
  const [inviting, setInviting] = useState(false);
  const [newGroup, setNewGroup] = useState(false);
  const [menu, setMenu] = useState<{ user: User; anchor: MenuAnchor } | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  // segurar o dedo no cartão abre o menu (o botão direito do mouse)
  const press = useLongPress();

  const memberOf = (id: string): RoomMember => members[id] ?? { userId: id, nick: null, avatarVersion: 0, modes: [], mainCharacterId: null, bars: [], lines: [] };
  const library = useApp((s) => s.library);
  const requestOpenSheet = useApp((s) => s.requestOpenSheet);
  const setMainCharacter = useApp((s) => s.setMainCharacter);
  const setLibraryOwner = useApp((s) => s.setLibraryOwner);
  const plugins = useApp((s) => s.plugins);
  const [editingCard, setEditingCard] = useState<User | null>(null);
  /** "Ver foto": a foto do cartão em tamanho grande */
  const [viewingPhoto, setViewingPhoto] = useState<{ src: string; name: string } | null>(null);
  /** a foto nesta mesa: o seletor de arquivo e de quem é a foto */
  const avatarInput = useRef<HTMLInputElement>(null);
  const avatarFor = useRef<string | null>(null);
  const pickRoomAvatar = (userId: string) => {
    avatarFor.current = userId;
    avatarInput.current?.click();
  };
  const saveRoomAvatar = async (file: File | undefined) => {
    const userId = avatarFor.current;
    avatarFor.current = null;
    if (!file || !userId) return;
    try {
      if (await setRoomAvatar(userId, await shrinkAvatar(file))) toast(t('users.roomAvatar.saved'), 'success');
    } catch {
      toast(t('config.profile.avatarInvalid'), 'error');
    }
  };
  /** "Criar uma ficha para…": para quem (o diálogo "Nova ficha" com o dono já escolhido) */
  const [newSheetFor, setNewSheetFor] = useState<string | null>(null);
  /** a barrinha sendo editada direto no cartão, e onde ela está na tela */
  const [barEdit, setBarEdit] = useState<{ userId: string; index: number; anchor: DOMRect; el: HTMLElement } | null>(null);
  // fechou pelo teclado: o foco volta para a barra
  const barEditEl = barEdit?.el;
  const closeBarEdit = useCallback(
    (byKeyboard: boolean) => {
      setBarEdit(null);
      if (byKeyboard && barEditEl?.isConnected) barEditEl.focus();
    },
    [barEditEl],
  );
  const setCardBar = useApp((s) => s.setCardBar);
  const setCardLine = useApp((s) => s.setCardLine);
  /** a linha sendo editada no lugar dela (`dono:índice`), e a que recebe o foco de volta pelo teclado */
  const [lineEdit, setLineEdit] = useState<string | null>(null);
  const refocusLine = useRef<string | null>(null);
  const characterOf = (m: RoomMember) => (m.mainCharacterId ? (library.find((i) => i.id === m.mainCharacterId) ?? null) : null);
  const myModes = me ? memberOf(me.id).modes : [];
  const iAdminister = !!me && (room?.ownerId === me.id || myModes.includes('gm'));
  const rules = room?.gameRules ?? DEFAULT_GAME_RULES;
  const viewer = { userId: me?.id ?? '', isGm: iAdminister };
  const visibleBars = (m: RoomMember) =>
    m.bars.slice(0, rules.barCount).flatMap((bar, i) => (bar ? [{ bar, i }] : []));
  /** cada um edita as próprias barrinhas; o mestre, as de todos (o servidor confere) */
  const canEditCard = (userId: string) => userId === me?.id || iAdminister;
  /**
   * as barras e as linhas do cartão: só as preenchidas, para todo mundo. As vazias não aparecem (o
   * cartão não fica com cara de rascunho); quem pode editar as preenche pelo menu do cartão
   * ("Barrinhas e linhas do cartão…") e clica nas que já têm valor para mudar.
   */
  const cardBars = (m: RoomMember): { bar: RoomMember['bars'][number] | null; i: number }[] => visibleBars(m);
  const cardLines = (m: RoomMember) => Array.from({ length: rules.lineCount }, (_, i) => ({ line: m.lines[i] ?? '', i })).filter(({ line }) => line);

  const groups = ROLE_ORDER.map((role) => ({
    role,
    users: users
      .filter((u) => roleOfModes(memberOf(u.id).modes) === role)
      .sort((a, b) => (memberOf(a.id).nick ?? a.displayName).localeCompare(memberOf(b.id).nick ?? b.displayName)),
  })).filter((g) => g.users.length > 0);

  /** O mestre dá uma ficha da biblioteca para a pessoa (tirando de quem a tinha). */
  async function giveSheet(user: User, name: string) {
    const people = roomPeople(users, members);
    const ownerHint = (ownerId: string | null) =>
      ownerId === null ? t('library.owner.none') : t('library.owner.of', { name: people.find((p) => p.id === ownerId)?.name ?? t('library.owner.away') });
    const itemId = await askChoice({
      title: t('users.menu.giveSheet', { name }),
      label: t('users.giveSheet.prompt', { name }),
      options: sheetsToGive(library, user.id).map((i) => ({ value: i.id, label: i.name, hint: ownerHint(i.ownerId) })),
    });
    if (itemId) setLibraryOwner(itemId, user.id);
  }

  /** O mestre escolhe o personagem principal da pessoa, entre as fichas dela. */
  async function chooseMain(user: User, name: string) {
    const current = memberOf(user.id).mainCharacterId;
    const NONE = '-';
    const choice = await askChoice({
      title: t('users.menu.chooseMain', { name }),
      label: t('users.chooseMain.prompt', { name }),
      options: [
        ...sheetsOf(library, user.id).map((i) => ({ value: i.id, label: i.name, current: i.id === current })),
        ...(current ? [{ value: NONE, label: t('users.chooseMain.none') }] : []),
      ],
    });
    if (choice) setMainCharacter(choice === NONE ? null : choice, user.id);
  }

  function entriesFor(user: User): MenuEntry[] {
    const member = memberOf(user.id);
    const role = roleOfModes(member.modes);
    const isMe = user.id === me?.id;
    const isOwner = user.id === room?.ownerId;
    const entries: MenuEntry[] = [{ kind: 'title', label: member.nick ? `${member.nick} (${user.displayName})` : user.displayName }];
    entries.push({ label: t('users.menu.profile'), onSelect: () => void viewProfile(user.id) });
    // a foto que o cartão mostra (a desta mesa, senão a do perfil), grande; sem foto, só a inicial
    const photo = avatarSrc(user.id, user.avatarVersion, room ? { id: room.id, avatarVersion: member.avatarVersion } : undefined);
    if (photo) entries.push({ label: t('users.menu.photo'), onSelect: () => setViewingPhoto({ src: photo, name: member.nick ?? user.displayName }) });
    const character = characterOf(member);
    if (character) entries.push({ label: t('users.menu.openSheet', { name: character.name }), onSelect: () => requestOpenSheet(character.id) });
    if (isMe || iAdminister) entries.push({ label: t('users.menu.card'), onSelect: () => setEditingCard(user) });
    // foto nesta mesa: a própria, ou a de qualquer um para o mestre (o "Alterar avatar do personagem" do original)
    if (isMe || iAdminister) {
      const name = member.nick ?? user.displayName;
      entries.push({ label: isMe ? t('users.menu.roomAvatar') : t('users.menu.roomAvatarOf', { name }), onSelect: () => pickRoomAvatar(user.id) });
      if (member.avatarVersion > 0)
        entries.push({
          label: isMe ? t('users.menu.roomAvatarRemove') : t('users.menu.roomAvatarRemoveOf', { name }),
          onSelect: () => void setRoomAvatar(user.id, null).then((ok) => ok && toast(t('users.roomAvatar.removed'), 'success')),
        });
    }
    if (isMe && character) entries.push({ label: t('users.menu.noMainCharacter'), onSelect: () => setMainCharacter(null) });
    if (!isMe) {
      if (canDm) entries.push({ label: t('users.menu.dm'), onSelect: () => openDm(user.id, user) });
      entries.push({ label: t('users.menu.pvt'), onSelect: () => openPrivate({ kind: 'pvt', to: user.id }) });
      entries.push({ label: t('users.menu.groupPvt'), onSelect: () => createGroup([user.id]) });
    }
    if (isMe) {
      entries.push({
        label: t('users.menu.nick'),
        onSelect: async () => {
          const next = await askText({ title: t('users.menu.nick'), label: t('users.menu.nick.prompt'), initial: member.nick ?? '', maxLength: 40, allowEmpty: true });
          if (next !== null) setNick(next || null);
        },
      });
    }
    // fichas para a pessoa (para si mesmo, o mestre usa a biblioteca)
    if (iAdminister && !isMe) {
      const name = member.nick ?? user.displayName;
      const theirs = sheetsOf(library, user.id);
      const sheets: MenuEntry[] = [];
      if (sheetSystems(plugins).length) sheets.push({ label: t(theirs.length ? 'users.menu.createAnotherSheet' : 'users.menu.createSheet', { name }), onSelect: () => setNewSheetFor(user.id) });
      if (sheetsToGive(library, user.id).length) sheets.push({ label: t('users.menu.giveSheet', { name }), onSelect: () => void giveSheet(user, name) });
      if (theirs.length > 1 || (theirs.length === 1 && !character)) sheets.push({ label: t('users.menu.chooseMain', { name }), onSelect: () => void chooseMain(user, name) });
      if (sheets.length) entries.push({ kind: 'separator' }, ...sheets);
    }
    // ninguém mexe no dono, a não ser ele mesmo
    if (iAdminister && (!isOwner || isMe)) {
      entries.push({ kind: 'separator' });
      if (role !== 'gm') entries.push({ label: t('users.menu.toGm'), onSelect: () => setRole(user.id, 'gm') });
      if (role !== 'player') entries.push({ label: t('users.menu.toPlayer'), onSelect: () => setRole(user.id, 'player') });
      if (role !== 'spectator') entries.push({ label: t('users.menu.toSpectator'), onSelect: () => setRole(user.id, 'spectator') });
      if (!isMe) {
        entries.push({ kind: 'separator' });
        const has = (mode: 'mute' | 'blind' | 'voice') => member.modes.includes(mode);
        entries.push({ label: t(has('mute') ? 'users.menu.unmute' : 'users.menu.mute'), onSelect: () => setFlag(user.id, 'mute', !has('mute')) });
        if (room?.moderated || has('voice')) {
          entries.push({ label: t(has('voice') ? 'users.menu.unvoice' : 'users.menu.voice'), onSelect: () => setFlag(user.id, 'voice', !has('voice')) });
        }
        entries.push({ label: t(has('blind') ? 'users.menu.unblind' : 'users.menu.blind'), onSelect: () => setFlag(user.id, 'blind', !has('blind')) });
        if (!isOwner) {
          entries.push({ kind: 'separator' });
          entries.push({ label: t('users.menu.kick'), danger: true, onSelect: () => kick(user.id, false) });
          entries.push({
            label: t('users.menu.ban'),
            danger: true,
            onSelect: async () => {
              const sure = await askConfirm({ title: t('users.menu.ban'), message: t('users.menu.ban.confirm', { name: user.displayName }), action: t('users.menu.ban'), danger: true });
              if (sure) kick(user.id, true);
            },
          });
        }
      }
    }
    return entries;
  }

  const openMenu = (user: User) => (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ user, anchor: { x: e.clientX, y: e.clientY } });
  };

  return (
    <div className="rd-users">
      <div className="rd-users__head">
        <div className="rd-users__actions">
          {canInvite ? <UserAction icon="userPlus" label={t('room.inviteFriends')} onClick={() => setInviting(true)} /> : null}
          {canDm && room && me && room.ownerId !== me.id ? (
            <UserAction icon="mail" label={t('room.talkToGm')} onClick={() => openDm(room.ownerId, users.find((u) => u.id === room.ownerId))} />
          ) : null}
          <UserAction icon="chat" label={t('users.newGroup')} onClick={() => setNewGroup(true)} />
          {voice ? (
            <UserAction
              icon={voice.inCall && !voice.micOn ? 'micOff' : 'mic'}
              label={voice.inCall ? t('room.voice.on') : t('room.voice')}
              name={voice.inCall ? t('room.voice.inCall') : t('room.voice')}
              className={voice.inCall ? 'is-on' : undefined}
              count={voice.inCall ? voice.peerCount + 1 : undefined}
              expanded={voiceOpen}
              onClick={() => setVoiceOpen((open) => !open)}
            />
          ) : null}
        </div>
      </div>
      {voice && voiceOpen ? (
        <div className="rd-users__conf">
          <ConferencePanel voice={voice} inline />
        </div>
      ) : null}
      {inviting ? <InviteFriendsDialog onClose={() => setInviting(false)} /> : null}
      {newGroup ? (
        <ChooseUsersDialog
          title={t('pvt.group.new.title')}
          action={t('pvt.group.new.action')}
          empty={t('pvt.group.new.empty')}
          users={users.filter((u) => u.id !== me?.id)}
          onClose={() => setNewGroup(false)}
          onChoose={createGroup}
        />
      ) : null}
      {groups.map((g) => (
        <section key={g.role} className="rd-users__group" aria-label={t(`users.group.${g.role}.many`)}>
          <h3 className="rd-users__groupTitle">{t(`users.group.${g.role}.${g.users.length === 1 ? 'one' : 'many'}`)}</h3>
          {g.users.map((u) => {
            const member = memberOf(u.id);
            const flags = (['mute', 'blind', 'voice'] as const).filter((f) => member.modes.includes(f));
            const names = cardNamesOf(member, characterOf(member), u);
            return (
              <div
                key={u.id}
                role="button"
                tabIndex={0}
                className="rd-users__item"
                onDoubleClick={() => void viewProfile(u.id)}
                {...press.bind((anchor) => setMenu({ user: u, anchor }))}
                onKeyDown={(e) => {
                  if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
                    e.preventDefault();
                    const r = e.currentTarget.getBoundingClientRect();
                    setMenu({ user: u, anchor: { x: r.left + 24, y: r.bottom - 8 } });
                  }
                }}
                title={u.displayName}
              >
                <CardAvatar
                  name={member.nick ?? u.displayName}
                  color={u.color}
                  userId={u.id}
                  avatarVersion={u.avatarVersion}
                  room={room ? { id: room.id, avatarVersion: member.avatarVersion } : undefined}
                  dragName={names.name}
                  video={videoOf(u.id)}
                  alone={!cardBars(member).length && !cardLines(member).length}
                />
                <span className="rd-users__info">
                  <span className="rd-users__name">{names.name}</span>
                  <span className="rd-users__login">
                    {names.account ?? (u.id === me?.id ? t('users.you') : t('users.online'))}
                    <span className="rd-users__presence" aria-label={t('users.online')} />
                    {typing[u.id] ? <span className="rd-users__typing">{t('users.typing')}</span> : null}
                  </span>
                  {cardBars(member).length ? (
                    <span className="rd-users__bars">
                      {cardBars(member).map(({ bar, i }) => {
                        const name = barName(rules, i, t('users.card.bar', { n: i + 1 }));
                        const text = bar ? barText(rules, i, bar, canSeeBarNumbers(rules, i, viewer, u.id)) : '';
                        const label = bar ? (text ? `${name}: ${text}` : name) : `${name}: ${t('users.bar.empty')}`;
                        if (!canEditCard(u.id))
                          return (
                            <span key={i} className="rd-users__bar" title={label} data-bar={i + 1}>
                              <span className="rd-users__barFill" style={{ width: `${bar ? Math.max(0, Math.min(1, bar.value / bar.max)) * 100 : 0}%`, background: barColor(rules, i, bar) }} />
                              <span className="rd-users__barText">{text}</span>
                            </span>
                          );
                        const open = (el: HTMLElement) => setBarEdit({ userId: u.id, index: i, anchor: el.getBoundingClientRect(), el });
                        return (
                          <span
                            key={i}
                            role="button"
                            tabIndex={0}
                            className={`rd-users__bar is-editable${bar ? '' : ' is-empty'}`}
                            title={`${label} · ${t('users.bar.editHint')}`}
                            aria-label={t('users.bar.edit', { name: label })}
                            data-bar={i + 1}
                            // o duplo clique no cartão abre o perfil: na barra, abre o editor
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              open(e.currentTarget);
                            }}
                            onClick={(e) => {
                              if ((e.nativeEvent as PointerEvent).pointerType === 'touch') open(e.currentTarget);
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter' && e.key !== 'F2') return;
                              e.preventDefault();
                              e.stopPropagation();
                              open(e.currentTarget);
                            }}
                          >
                            {bar ? <span className="rd-users__barFill" style={{ width: `${Math.max(0, Math.min(1, bar.value / bar.max)) * 100}%`, background: barColor(rules, i, bar) }} /> : null}
                            <span className="rd-users__barText">{text}</span>
                          </span>
                        );
                      })}
                    </span>
                  ) : null}
                  {cardLines(member).map(({ line, i }) => {
                    const ruleName = lineRule(rules, i).name;
                    if (!canEditCard(u.id))
                      return (
                        <span key={i} className="rd-users__line" title={ruleName || undefined} data-line={i + 1}>
                          {line}
                        </span>
                      );
                    const key = `${u.id}:${i}`;
                    const name = ruleName || t('users.card.line', { n: i + 1 });
                    if (lineEdit === key)
                      return (
                        <LineEditor
                          key={i}
                          text={line}
                          label={name}
                          onSave={(text) => setCardLine(u.id, i, text)}
                          onClose={(byKeyboard) => {
                            if (byKeyboard) refocusLine.current = key;
                            setLineEdit(null);
                          }}
                        />
                      );
                    const label = line ? `${name}: ${line}` : `${name}: ${t('users.bar.empty')}`;
                    return (
                      <span
                        key={i}
                        ref={(el) => {
                          // fechou pelo teclado: o foco volta para a linha
                          if (el && refocusLine.current === key) {
                            refocusLine.current = null;
                            el.focus();
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        className={`rd-users__line is-editable${line ? '' : ' is-empty'}`}
                        title={`${label} · ${t('users.line.editHint')}`}
                        aria-label={t('users.line.edit', { name: label })}
                        data-line={i + 1}
                        // o duplo clique no cartão abre o perfil: na linha, edita a linha
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          setLineEdit(key);
                        }}
                        onClick={(e) => {
                          if ((e.nativeEvent as PointerEvent).pointerType === 'touch') setLineEdit(key);
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== 'F2') return;
                          e.preventDefault();
                          e.stopPropagation();
                          setLineEdit(key);
                        }}
                      >
                        {line || name}
                      </span>
                    );
                  })}
                  {flags.length ? (
                    <span className="rd-users__flags">
                      {flags.map((f) => (
                        <span key={f} className={`rd-users__flag rd-users__flag--${f}`}>
                          {t(`users.mode.${f}`)}
                        </span>
                      ))}
                    </span>
                  ) : null}
                </span>
                <button type="button" className="rd-users__more" aria-label={t('users.menu', { name: u.displayName })} onClick={openMenu(u)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <circle cx="5" cy="12" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="19" cy="12" r="2" />
                  </svg>
                </button>
              </div>
            );
          })}
        </section>
      ))}
      {menu ? <ContextMenu anchor={menu.anchor} entries={entriesFor(menu.user)} label={t('users.menu', { name: menu.user.displayName })} onClose={closeMenu} /> : null}
      {viewingPhoto ? <ImageViewerDialog src={viewingPhoto.src} name={viewingPhoto.name} fill onClose={() => setViewingPhoto(null)} /> : null}
      {editingCard ? <CardDialog user={editingCard} member={memberOf(editingCard.id)} rules={rules} onClose={() => setEditingCard(null)} /> : null}
      {newSheetFor ? <NewSheetDialog parentId={null} ownerId={newSheetFor} onClose={() => setNewSheetFor(null)} /> : null}
      <input
        ref={avatarInput}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        hidden
        aria-label={t('users.menu.roomAvatar')}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          void saveRoomAvatar(file);
        }}
      />
      {barEdit
        ? (() => {
            const bar = memberOf(barEdit.userId).bars[barEdit.index] ?? null;
            return (
              <BarEditor
                anchor={barEdit.anchor}
                name={barName(rules, barEdit.index, t('users.card.bar', { n: barEdit.index + 1 }))}
                color={barColor(rules, barEdit.index, bar)}
                bar={bar}
                onApply={(next) => setCardBar(barEdit.userId, barEdit.index, next?.value ?? 0, next?.max ?? 0)}
                onClose={closeBarEdit}
              />
            );
          })()
        : null}
    </div>
  );
}

/**
 * Um atalho do alto do painel: o ícone num círculo e o nome embaixo, em blocos iguais. `name` é o
 * nome para o leitor de tela, quando diz mais que o texto; `count`, o número no canto do ícone (quantos
 * estão na conferência); `expanded`, o atalho que abre algo ali mesmo (a conferência).
 */
function UserAction({
  icon,
  label,
  name,
  className,
  count,
  expanded,
  onClick,
}: {
  icon: IconName;
  label: string;
  name?: string;
  className?: string;
  count?: number;
  expanded?: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" className={`rd-users__action${className ? ` ${className}` : ''}`} aria-label={name} aria-expanded={expanded} onClick={onClick}>
      <span className="rd-users__actionIcon" aria-hidden="true">
        <Icon name={icon} size={18} />
        {count ? <span className="rd-users__actionCount">{count}</span> : null}
      </span>
      <span className="rd-users__actionText">{label}</span>
    </button>
  );
}

/**
 * A foto do cartão, do tamanho do texto ao lado (`cardAvatarSize`): mede a coluna de texto (o
 * irmão logo depois) e o cartão já no primeiro quadro, e de novo quando aparecem barras ou linhas
 * ou o painel muda de largura. `alone`: o cartão não tem barras nem linhas, e a foto fica maior.
 */
function CardAvatar({ alone, ...props }: Omit<ComponentProps<typeof Avatar>, 'size'> & { alone: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [size, setSize] = useState(alone ? CARD_AVATAR_ALONE : CARD_AVATAR_MIN);
  useLayoutEffect(() => {
    const wrap = ref.current;
    const card = wrap?.parentElement;
    const text = wrap?.nextElementSibling;
    if (!wrap || !card || !(text instanceof HTMLElement)) return;
    const measure = () => {
      // painel escondido (acordeão fechado, aba de trás): fica a última medida
      if (!card.clientWidth && !text.offsetHeight) return;
      setSize(cardAvatarSize(text.offsetHeight, card.clientWidth, alone));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(text);
    observer.observe(card);
    return () => observer.disconnect();
  }, [alone]);
  return (
    <span ref={ref} className="rd-users__avatar">
      <Avatar {...props} size={size} />
    </span>
  );
}

/**
 * Linha do cartão editada no lugar dela (o `TVerticalPresenceEditableLine` do original). O cursor
 * fica no fim do texto, sem nada selecionado: uma digitação rápida não apaga o que já estava. Enter
 * ou clicar fora grava; Esc desiste e deixa o texto de antes.
 */
function LineEditor({ text, label, onSave, onClose }: { text: string; label: string; onSave: (text: string) => void; onClose: (byKeyboard: boolean) => void }) {
  const input = useRef<HTMLInputElement>(null);
  /** já gravou ou desistiu (Enter e depois o blur não gravam duas vezes) */
  const done = useRef(false);
  useLayoutEffect(() => {
    const el = input.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  const finish = (save: boolean, byKeyboard: boolean) => {
    if (done.current) return;
    done.current = true;
    if (save) onSave(input.current?.value ?? text);
    onClose(byKeyboard);
  };
  return (
    <input
      ref={input}
      className="rd-users__lineInput"
      defaultValue={text}
      maxLength={MEMBER_LIMITS.lineLength}
      aria-label={label}
      // o cartão em volta abre o perfil com duplo clique e o menu pelo teclado: aqui é só texto
      onDoubleClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
          e.preventDefault();
          finish(true, true);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          finish(false, true);
        }
      }}
      onBlur={() => finish(true, false)}
    />
  );
}

/** Barrinhas e linhas do cartão, editadas à mão (os plugins também as preenchem pela ficha). */
function CardDialog({ user, member, rules, onClose }: { user: User; member: RoomMember; rules: GameRules; onClose: () => void }) {
  const t = useT();
  const setBar = useApp((s) => s.setCardBar);
  const setLine = useApp((s) => s.setCardLine);
  const [bars, setBars] = useState(() => Array.from({ length: MEMBER_LIMITS.bars }, (_, i) => ({ value: member.bars[i] ? String(member.bars[i]!.value) : '', max: member.bars[i] ? String(member.bars[i]!.max) : '' })));
  const [lines, setLines] = useState(() => Array.from({ length: MEMBER_LIMITS.lines }, (_, i) => member.lines[i] ?? ''));
  // só manda o que mudou: cada envio é uma mudança no cartão (e um aviso no chat)
  const save = () => {
    bars.slice(0, rules.barCount).forEach((b, i) => {
      const value = Number(b.value.replace(',', '.'));
      const max = Number(b.max.replace(',', '.'));
      const ok = b.max.trim() !== '' && Number.isFinite(max) && max > 0 && Number.isFinite(value);
      const before = member.bars[i];
      const next = ok ? { value, max } : null;
      if (next ? !before || before.value !== next.value || before.max !== next.max : before) setBar(user.id, i, next?.value ?? 0, next?.max ?? 0);
    });
    lines.slice(0, rules.lineCount).forEach((text, i) => {
      if (text.trim() !== (member.lines[i] ?? '')) setLine(user.id, i, text);
    });
    onClose();
  };
  return (
    <Dialog
      title={t('users.card.title', { name: member.nick ?? user.displayName })}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('create.cancel')}
          </Button>
          <Button variant="action" onClick={save}>
            {t('scene.save')}
          </Button>
        </>
      }
    >
      <form
        className="rd-cardForm"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <p className="rd-prompt__label">{t('users.card.hint')}</p>
        {bars.slice(0, rules.barCount).map((b, i) => {
          const name = barName(rules, i, t('users.card.bar', { n: i + 1 }));
          return (
            <div key={i} className="rd-cardForm__bar">
              <span className="rd-cardForm__swatch" style={{ background: barColor(rules, i, member.bars[i]) }} />
              <span className="rd-cardForm__name" title={name}>
                {name}
              </span>
              <TextEdit look="filled" value={b.value} inputMode="decimal" maxLength={8} aria-label={t('users.card.value', { name })} onChange={(v) => setBars((all) => all.map((x, j) => (j === i ? { ...x, value: v } : x)))} />
              <span>/</span>
              <TextEdit look="filled" value={b.max} inputMode="decimal" maxLength={8} aria-label={t('users.card.max', { name })} onChange={(v) => setBars((all) => all.map((x, j) => (j === i ? { ...x, max: v } : x)))} />
            </div>
          );
        })}
        {lines.slice(0, rules.lineCount).map((line, i) => {
          const name = lineRule(rules, i).name || t('users.card.line', { n: i + 1 });
          return <TextEdit key={i} look="filled" value={line} maxLength={MEMBER_LIMITS.lineLength} placeholder={name} aria-label={name} onChange={(v) => setLines((all) => all.map((x, j) => (j === i ? v : x)))} />;
        })}
        {rules.barCount === 0 && rules.lineCount === 0 ? <p className="rd-prompt__label">{t('users.card.nothing')}</p> : null}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
